import {type} from 'arktype';
import BigNumber from 'bignumber.js';
import {BTC, ETH} from '~/entities/currencies';
import {invariant} from '~/library/toolbox';
import {AccountId, BaseCurrencyCode, Resource, Source, TransactionId} from '~/library/types';
import {selectAssetsResource} from '~/redux/assets/assetsResource';
import {selectFinancesResource} from '~/redux/finances';
import {Assets, Crypto, Fiat, Token} from '~/shared/api/legacy_services/Assets/Assets';
import {bindTracedSelector} from '~/shared/store';

export const OrderId = type('number').as<TransactionId>();
export type OrderId = typeof OrderId.infer;

export interface BuyTxResource {
    accountFrom: FiatAccount;
    accountTo: BlockchainAccount;
    minimum: BigNumber;
    absoluteFee: BigNumber;
    /** percent of the amount entered by the user */
    relativeFee: BigNumber;
}

export const useBuyTxResource = bindTracedSelector(selectBuyTxResource);

function selectBuyTxResource(state: AppModel, from: AccountId, to: AccountId) {
    return Resource.all(
        selectAssetsResource(state),
        selectFinancesResource(state).mapSuccess(r => r.operationsSettings)
    ).mapSuccess(([assets, settings]): BuyTxResource | null => {
        const {fiatAccounts, blockchainAccounts} = selectAccountsFromAssets(assets);

        const accountFrom = fiatAccounts.find(({id}) => id === from);
        const accountTo = blockchainAccounts.find(({id}) => id === to);

        if (!accountFrom || !accountTo) {
            return null;
        }

        const minimum = assets.minimum.default[accountFrom.code];

        invariant(!!minimum, `Minimum amount for ${accountTo.code} not found`);

        const absoluteFee = (() => {
            switch (true) {
                case accountTo.code !== accountTo.blockchain.code:
                    return settings.buy_fee_erc20_absolute;
                case accountTo.code === ETH:
                    return settings.buy_fee_eth_absolute;
                case accountTo.code === BTC:
                    return settings.buy_fee_btc_absolute;
                default:
                    throw new Error(`Fee for buying ${accountTo.code} not found`);
            }
        })();

        const relativeFee =
            accountTo.type === 'stablecoin' ? settings.exchange_stablecoin_relative : settings.exchange_crypto_relative;

        return {
            accountFrom,
            accountTo,
            minimum: BigNumber(minimum),
            absoluteFee: BigNumber(absoluteFee),
            relativeFee: BigNumber(relativeFee),
        };
    });
}

interface AccountBase {
    id: AccountId;
    name: string;
    code: BaseCurrencyCode;
    balance: string;
    decimals: number;
    icon: Source | null;
}

export interface FiatAccount extends AccountBase {
    type: 'fiat';
    decimals: number;
}

export type BlockchainAccountType = 'crypto' | 'stablecoin';

export interface BlockchainAccount extends AccountBase {
    type: BlockchainAccountType;
    decimals: number;
    blockchain: {
        code: BaseCurrencyCode;
        decimals: number;
    };
}

function mapAccountBase(account: Crypto | Token | Fiat): AccountBase {
    return {
        id: account.id,
        name: account.name,
        code: account.currency,
        decimals: account.decimals,
        balance: account.balance,
        icon: account.icon,
    };
}

function selectAccountsFromAssets(assets: Assets) {
    const {crypto, tokens, fiat} = assets.accounts;

    const fiatAccounts = fiat.map(
        (account): FiatAccount => ({
            ...mapAccountBase(account),
            type: 'fiat',
        })
    );

    const blockchainAccounts = (
        [
            {type: 'crypto', accounts: crypto},
            {type: 'stablecoin', accounts: tokens},
        ] as const
    ).flatMap(({type, accounts}) =>
        accounts
            .map((account): BlockchainAccount | null => {
                const blockchainAccount =
                    account.blockchain.currency === account.currency
                        ? account
                        : crypto.find(c => c.currency === account.blockchain.currency);

                if (!blockchainAccount) {
                    return null;
                }

                const blockchain = {
                    code: blockchainAccount.currency,
                    decimals: blockchainAccount.decimals,
                };
                return {
                    ...mapAccountBase(account),
                    type: type,
                    blockchain: blockchain,
                };
            })
            .filter(account => !!account)
    );

    return {fiatAccounts, blockchainAccounts};
}
